import { useState, useEffect, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { linkedProductVariants } from 'api/mdl22'

const normalizeToUI = t => ({
  Name: t.Name ?? t.VariantName,
  Code: t.Code ?? t.VariantCode,
  Color: t.Color ?? t.VariantColor,
  VariantDetailId: t.DetailId ?? t.VariantDetailId,
  IdProductColor: t.IdProductColor ?? '00000000-0000-0000-0000-000000000000',
})

const toDomain = t => ({
  Name: t.Name,
  Code: t.Code,
  Color: t.Color,
  DetailId: t.VariantDetailId,
  IdProductColor: t.IdProductColor ?? '00000000-0000-0000-0000-000000000000',
})

export const useTintsController = ({
  sku,
  hasVariant,
  initialTints = [],
  controlledTints,
  syncKey,
  onChange,
}) => {
  const shouldFetch = !!hasVariant && !!sku
  const isControlled = controlledTints !== undefined

  // Fetch
  const {
    data: tints = [],
    isLoading: isLoadingTints,
    isError: isErrorTints,
  } = useQuery({
    queryKey: ['tints', sku],
    queryFn: async () => {
      const response = await linkedProductVariants({ code: sku })
      return response?.Data?.[0]?.Variants || []
    },
    enabled: shouldFetch,
  })

  // State
  const [selectedTints, setSelectedTints] = useState(() => {
    const base = isControlled ? controlledTints : initialTints
    return (base ?? []).map(normalizeToUI)
  })

  // Controlled sync
  useEffect(() => {
    if (!isControlled) return

    setSelectedTints(prev => {
      const next = (controlledTints ?? []).map(normalizeToUI)
      if (JSON.stringify(prev) === JSON.stringify(next)) return prev
      return next
    })
  }, [controlledTints, syncKey, isControlled])

  // Domain output
  const formattedSelectedTints = useMemo(
    () => selectedTints.map(toDomain),
    [selectedTints]
  )

  // Actions
  const toggleTint = useCallback(
    tint => {
      const uiTint = normalizeToUI(tint)

      setSelectedTints(prev => {
        const exists = prev.some(
          t => t.VariantDetailId === uiTint.VariantDetailId
        )

        const next = exists
          ? prev.filter(t => t.VariantDetailId !== uiTint.VariantDetailId)
          : [...prev, uiTint]

        onChange?.(next.map(toDomain))
        return next
      })
    },
    [onChange]
  )

  // Helpers
  const isSelected = useCallback(
    tint => selectedTints.some(t => t.VariantDetailId === tint.VariantDetailId),
    [selectedTints]
  )

  return {
    // data
    tints,
    selectedTints,
    formattedSelectedTints,

    // network
    isLoadingTints,
    isErrorTints,
    shouldShow: shouldFetch,

    // actions
    toggleTint,

    // helpers
    isSelected,
    count: selectedTints.length,
  }
}
